import api from "./api";

export interface EvidenceAnalysisResult {
  success: boolean;
  analysis?: string;
  confidence?: number;
  extractedText?: string;
  error?: string;
  tokenCost?: {
    model: string;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    totalCost: number;
  };
}

export interface FileAnalysisResult {
  index: number;
  type: "file";
  success: boolean;
  fileInfo?: {
    originalName: string;
    mimeType: string;
    size: number;
    sha256Hash: string;
  };
  evidenceData?: {
    type: string;
    description: string;
    source: string;
    timestamp: string;
    sha256_hash: string;
    fileName: string;
    cloudinaryUrl?: string;
    cloudinaryPublicId?: string;
    cloudinaryFormat?: string;
    fileSize?: number;
  };
  analysis?: EvidenceAnalysisResult;
  fileName?: string;
  error?: string;
}

export interface UrlAnalysisResult {
  index: number;
  type: "youtube" | "url";
  success: boolean;
  fileInfo?: {
    youtubeUrl?: string;
    url?: string;
    type: string;
    sha256Hash: string;
  };
  evidenceData?: {
    type: string;
    description: string;
    source: string;
    timestamp: string;
    sha256_hash: string;
    fileName: string;
    youtubeUrl?: string;
    url?: string;
  };
  analysis?: EvidenceAnalysisResult;
  url?: string;
  error?: string;
}

export interface UnifiedEvidenceResponse {
  success: boolean;
  results: {
    files: FileAnalysisResult[];
    youtubeUrls: UrlAnalysisResult[];
    urls: UrlAnalysisResult[];
  };
  analysisTexts: string[];
  summary: {
    total: number;
    successful: number;
    failed: number;
    byType: {
      files: number;
      youtubeUrls: number;
      urls: number;
    };
    processingTimeMs: number;
  };
  message: string;
  error?: string;
}

export interface UnifiedEvidenceRequest {
  files?: File[];
  youtubeUrls?: string[];
  urls?: string[];
  evidenceType?: string;
  description?: string;
  source?: "Buyer" | "Seller" | "Disputer" | "PoolOwner";
  question?: string;
  options?: string[];
}

/**
 * Analyze multiple evidence items in a single request
 * Supports files (images, PDFs, documents, videos), YouTube URLs, and generic web URLs
 */
export const analyzeUnifiedEvidence = async (
  request: UnifiedEvidenceRequest,
  onProgress?: (progress: number) => void
): Promise<UnifiedEvidenceResponse> => {
  const formData = new FormData();

  // Add files
  if (request.files && request.files.length > 0) {
    request.files.forEach((file) => {
      formData.append("evidenceFiles", file);
    });
  }

  // Add YouTube URLs
  if (request.youtubeUrls && request.youtubeUrls.length > 0) {
    formData.append("youtubeUrls", JSON.stringify(request.youtubeUrls));
  }

  // Add generic URLs
  if (request.urls && request.urls.length > 0) {
    formData.append("urls", JSON.stringify(request.urls));
  }

  // Add metadata
  if (request.evidenceType) {
    formData.append("evidenceType", request.evidenceType);
  }
  if (request.description) {
    formData.append("description", request.description);
  }
  if (request.source) {
    formData.append("source", request.source);
  }

  // Add Rain context if provided
  if (request.question) {
    formData.append("question", request.question);
  }
  if (request.options && request.options.length > 0) {
    formData.append("options", JSON.stringify(request.options));
  }

  const response = await api.post<UnifiedEvidenceResponse>(
    "/api/files/analyze-unified",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(progress);
        }
      },
    }
  );

  return response.data;
};

/**
 * Helper function to validate a YouTube URL
 */
export const isValidYouTubeUrl = (url: string): boolean => {
  const youtubeRegex =
    /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/)|youtu\.be\/)[\w-]+/;
  return youtubeRegex.test(url);
};

/**
 * Helper function to validate a generic URL
 */
export const isValidUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === "http:" || urlObj.protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * Helper function to determine evidence type from file
 */
export const getEvidenceTypeFromFile = (
  file: File
): "photo" | "pdf" | "video" | "text_note" => {
  if (file.type.startsWith("image/")) return "photo";
  if (file.type.startsWith("video/")) return "video";
  if (file.type === "application/pdf") return "pdf";
  return "text_note";
};

/**
 * Generate SHA256 hash from a file
 */
export const generateSHA256 = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Generate SHA256 hash from a string (for URLs)
 */
export const generateSHA256FromString = async (text: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

export default {
  analyzeUnifiedEvidence,
  isValidYouTubeUrl,
  isValidUrl,
  getEvidenceTypeFromFile,
  generateSHA256,
  generateSHA256FromString,
};
